import {
	Button,
	CircularProgress,
	Grid,
	Snackbar,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { Alert, Color } from "@material-ui/lab";
import React, { useEffect, useState } from "react";
import { Root } from "../../models/Root";
import { deleteRoot, getRootById } from "../../repositories/Root";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		checkbox: {
			userSelect: "none",
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	rootIdForDelete: number | null;
};

export const DeleteRoot = ({ show, onHide, rootIdForDelete }: Props) => {
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);
	const [rootForDelete, setRootForDelete] = useState<Root | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const classes = useStyles();

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const handleDelete = async () => {
		setIsSubmitLoading(true);
		try {
			const res = await deleteRoot(rootIdForDelete);
			if (res.ok) {
				setMessage(" The root was deleted successfully");
				setMessageType("success");
				onHide(true);
				setRootForDelete(null);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			(async () => {
				setIsLoading(true);
				const { root } = await getRootById(rootIdForDelete);
				if (root?.success) {
					setRootForDelete(root.result);
				} else {
					onHide(true);
					setMessage("Error, root not found. Try again");
					setOpenAlertMessage(true);
					setRootForDelete(null);
				}

				setIsLoading(false);
			})();
		}
	}, [show]);

	return (
		<>
			<>
				<Modal
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="deleteRoot"
					title={"Delete a root"}
					maxWidth="sm"
				>
					<Modal.Content>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "60px" }}
							>
								{/* <LazyLoading /> */}
								<CircularProgress size="3rem" />
							</Grid>
						)}

						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Typography>
										You will not be able to recover deleted data
									</Typography>
								</Grid>
								<br />
								<Grid container spacing={1}>
									<Typography>
										Are you sure you want to delete{" "}
										<strong> {rootForDelete?.rootName}</strong>?
									</Typography>
								</Grid>
							</>
						)}
					</Modal.Content>
					<Modal.Actions style={{ padding: "1rem" }}>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => {
										onHide(false);
										setRootForDelete(null);
									}}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={
										isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
									}
									disabled={isSubmitLoading}
									variant="contained"
									color="secondary"
									onClick={handleDelete}
								>
									Delete
								</Button>
							</Grid>
						</Grid>
					</Modal.Actions>
				</Modal>
				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
